import { useState } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  Button,
  DropdownMenu,
  ListFilterIcon,
  Tooltip,
} from '@buffer-mono/popcorn'

import { ChannelFilterSubMenu } from './ChannelFilterSubMenu'
import type { ChannelFilterSubMenuChannel } from './ChannelFilterSubMenu'
import { StatusFilterSubMenu } from './StatusFilterSubMenu'
import {
  DATE_RANGES,
  DEFAULT_DATE_RANGE,
  DEFAULT_SORT_OPTION,
  DEFAULT_STATUS,
  SORT_OPTION_LABELS,
  isDateRange,
} from './types'
import type {
  CommunityEntity,
  DateRange,
  FilterStatus,
  SortOption,
} from './types'
import { useDelayedHover } from '../hooks/ui/useDelayedHover'

import styles from './FilterAndSortMenu.module.css'

type FilterAndSortMenuProps = {
  sortOption?: SortOption
  publishedAt?: DateRange
  status?: FilterStatus
  supportsUnansweredFirst?: boolean
  onSortOptionChange: (value: SortOption) => void
  onPublishedAtChange: (value: DateRange) => void
  onResetFilters: () => void
  // On narrow headers the inline channel/status controls are hidden, so this
  // menu hosts them alongside sort + date — keeping all filtering under one
  // funnel and leaving the resolve menu for resolve actions only.
  showCollapsedFilters?: boolean
  onStatusChange?: (status: FilterStatus) => void
  // Channel filter is only relevant on views that show it (AllChannelsView).
  channels?: readonly ChannelFilterSubMenuChannel[]
  selectedChannelIds?: string[]
  onChannelSelect?: (channelIds: string[]) => void
  disabledChannelIds?: Set<string>
  getDisabledChannelReason?: (channelId: string) => React.ReactNode
  // Names the entity in the trigger tooltip.
  entity?: CommunityEntity
}

const isValidSortOption = (value: string): value is SortOption => {
  return Object.keys(SORT_OPTION_LABELS).includes(value)
}

export const FilterAndSortMenu = ({
  sortOption = DEFAULT_SORT_OPTION,
  publishedAt = DEFAULT_DATE_RANGE,
  status,
  supportsUnansweredFirst = true,
  onSortOptionChange,
  onPublishedAtChange,
  onResetFilters,
  showCollapsedFilters = false,
  onStatusChange,
  channels,
  selectedChannelIds = [],
  onChannelSelect,
  disabledChannelIds,
  getDisabledChannelReason,
  entity = 'comments',
}: FilterAndSortMenuProps): JSX.Element => {
  // "Unanswered first" only makes sense when the entity supports it AND all
  // statuses are shown together — filtering to a single status makes the
  // sort a no-op.
  const showUnansweredFirst =
    supportsUnansweredFirst && (!status || status === 'all')
  // The effective default differs by context: when "Unanswered first" is
  // hidden, the default the query applies is "newest".
  const effectiveDefaultSortOption: SortOption = showUnansweredFirst
    ? DEFAULT_SORT_OPTION
    : 'newest'
  // The sort param in the URL is shared across tabs, so a hidden option can
  // still arrive as the stored value (e.g. set on the Comments tab, then
  // switch to Mentions). Display what the query actually applies.
  const effectiveSortOption: SortOption =
    !showUnansweredFirst && sortOption === 'unansweredFirst'
      ? 'newest'
      : sortOption
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const { handleMouseEnter, handleMouseLeave } = useDelayedHover({
    onOpen: () => setShowTooltip(true),
    onClose: () => setShowTooltip(false),
    openDelayDuration: 1000,
    closeDelayDuration: 100,
    isOpen: showTooltip,
  })

  // Get active filters count to show on the button
  const getActiveFiltersCount = (): number => {
    let count = 0
    if (effectiveSortOption !== effectiveDefaultSortOption) count += 1
    if (publishedAt && publishedAt !== DEFAULT_DATE_RANGE) count += 1
    // On narrow headers the channel/status filters live in this menu too, so
    // reflect them in the button's active-state indicator.
    if (showCollapsedFilters) {
      if (status && status !== DEFAULT_STATUS) count += 1
      if (selectedChannelIds.length > 0) count += 1
    }
    return count
  }

  const activeFiltersCount = getActiveFiltersCount()

  // Create type-safe handlers for RadioGroup
  const handleSortChange = (value: string): void => {
    if (!isValidSortOption(value)) {
      // eslint-disable-next-line no-console
      console.error(
        'Invalid sort option value:',
        value,
        'Expected one of:',
        Object.keys(SORT_OPTION_LABELS),
      )
      return
    }

    onSortOptionChange(value)
  }

  const handleDateRangeChange = (value: string): void => {
    if (!isDateRange(value)) {
      // eslint-disable-next-line no-console
      console.error(
        'Invalid date range value:',
        value,
        'Expected one of:',
        Object.keys(DATE_RANGES),
      )
      return
    }

    onPublishedAtChange(value)
  }

  const triggerLabel =
    entity === 'mentions'
      ? 'Filter and sort mentions'
      : 'Filter and sort comments'

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <Tooltip content={triggerLabel} open={showTooltip && !isDropdownOpen}>
        <DropdownMenu.Trigger asChild>
          <Button
            size="medium"
            variant="tertiary"
            aria-label={triggerLabel}
            data-testid="filter-sort-button"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={clsx(
              styles.filters,
              activeFiltersCount > 0 && styles.activeFilters,
            )}
          >
            <ListFilterIcon />
          </Button>
        </DropdownMenu.Trigger>
      </Tooltip>

      <DropdownMenu.Content align="start" data-no-focus-lock>
        {showCollapsedFilters && (
          <>
            {channels && channels.length > 0 && onChannelSelect && (
              <ChannelFilterSubMenu
                channels={channels}
                selectedChannelIds={selectedChannelIds}
                onChannelSelect={onChannelSelect}
                disabledChannelIds={disabledChannelIds}
                getDisabledChannelReason={getDisabledChannelReason}
              />
            )}
            {onStatusChange && (
              <StatusFilterSubMenu
                status={status ?? DEFAULT_STATUS}
                onStatusChange={onStatusChange}
              />
            )}
            <DropdownMenu.Separator />
          </>
        )}
        <DropdownMenu.RadioGroup
          label="Sort By"
          value={effectiveSortOption}
          onChange={handleSortChange}
        >
          {showUnansweredFirst && (
            <DropdownMenu.RadioItem value="unansweredFirst">
              Unanswered first
            </DropdownMenu.RadioItem>
          )}
          <DropdownMenu.RadioItem value="newest">Newest</DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="oldest">Oldest</DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>

        <DropdownMenu.Separator />

        <DropdownMenu.RadioGroup
          label="Date Range"
          value={publishedAt}
          onChange={handleDateRangeChange}
        >
          <DropdownMenu.RadioItem value="last7days">
            Last 7 days
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="last30days">
            Last 30 days
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="last6months">
            Last 6 months
          </DropdownMenu.RadioItem>
          <DropdownMenu.RadioItem value="lastyear">
            Last 12 months
          </DropdownMenu.RadioItem>
        </DropdownMenu.RadioGroup>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onClick={onResetFilters}>
          Reset filters
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
